import type { ReactNode } from 'react';
import { cn } from '@sara/ui/lib/utils';

interface PageHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  meta?: ReactNode;
  size?: 'default' | 'compact';
  className?: string;
}

export function PageHeader({
  title,
  description,
  action,
  meta,
  size = 'default',
  className,
}: PageHeaderProps) {
  const hasAside = Boolean(meta || action);
  const stackedAside = Boolean(meta && action);

  return (
    <header
      className={cn(
        'flex flex-wrap items-start gap-x-4 gap-y-3',
        'sm:grid sm:grid-cols-[minmax(0,1fr)_auto] sm:gap-x-8 sm:gap-y-4',
        className
      )}
    >
      <h1
        className={cn(
          'font-display font-medium leading-[1.05] tracking-tight text-balance text-foreground',
          size === 'default'
            ? 'text-[30px] sm:text-[36px] md:text-[44px]'
            : 'text-[26px] sm:text-[32px] md:text-[40px]'
        )}
      >
        {title}
      </h1>
      {hasAside && (
        <div
          className={cn(
            'ml-auto flex flex-col items-end gap-2',
            'sm:col-start-2 sm:row-start-1 sm:row-end-3 sm:ml-0 sm:gap-0 sm:self-stretch',
            stackedAside ? 'sm:justify-between' : 'sm:justify-end'
          )}
        >
          {meta}
          {action}
        </div>
      )}
      {description && (
        <p
          className={cn(
            'basis-full max-w-xl text-[14px] leading-relaxed text-muted-foreground',
            'sm:col-span-1 sm:col-start-1 sm:row-start-2 sm:text-[15px]'
          )}
        >
          {description}
        </p>
      )}
    </header>
  );
}
